import { Link } from "react-router-dom";
import { LOGO_URL } from "@/lib/brand";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/LanguageContext";
import { HexMark, HexWatermark } from "@/components/BrandMark";

export default function Footer() {
  const { t } = useLang();

  const cols = [
    { title: t.footer.col_materials, to: "/products", links: t.footer.links_materials },
    { title: t.footer.col_services, to: "/services", links: t.footer.links_services },
    {
      title: t.footer.col_company,
      links: t.footer.links_company,
      routes: ["/about", "/innovation", "/sustainability", "/industries", "/contact"],
    },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-primary text-primary-foreground overflow-hidden"
    >
      {/* Subtle hex pattern background */}
      <div className="absolute inset-0 hex-pattern-light opacity-50 pointer-events-none" />

      {/* Oversized hex brand watermark */}
      <div className="absolute inset-y-0 right-0 flex items-center pointer-events-none">
        <HexWatermark
          opacity={0.07}
          size={760}
          strokeWidth={1.4}
          className="text-white -right-40 top-1/2 -translate-y-1/2"
        />
      </div>

      <div className="relative container-x py-20 sm:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-6">
              <img
                src={LOGO_URL}
                alt="BALTIVEX"
                className="h-28 w-28 object-contain"
                style={{
                  mixBlendMode: "screen",
                  filter: "brightness(1.05) contrast(1.05)",
                }}
              />
              <div>
                <div className="font-display text-3xl font-medium tracking-tight">
                  BALTIVEX
                </div>
                <div className="text-[11px] tracking-[0.25em] uppercase text-white/60 mt-1">
                  {t.footer.tagline}
                </div>
              </div>
            </div>

            <p className="mt-10 text-white/70 max-w-md leading-relaxed">
              {t.footer.intro}
            </p>

            <Link
              to="/contact"
              data-testid="footer-cta"
              className="group inline-flex items-center gap-2 mt-10 border border-white/30 px-6 py-3.5 text-sm tracking-wide hover:bg-white hover:text-primary transition-all"
            >
              {t.footer.cta}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-10">
            {cols.map((c) => (
              <div key={c.title}>
                <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-white/50 font-semibold">
                  <HexMark size={10} strokeWidth={1.6} className="text-accent" />
                  <span>{c.title}</span>
                </div>
                <ul className="mt-5 space-y-3">
                  {c.links.map((label, i) => {
                    const to = c.routes ? c.routes[i] : c.to;
                    return (
                      <li key={`${c.title}-${i}`}>
                        <Link
                          to={to}
                          className="text-sm text-white/80 hover:text-white transition-colors link-underline"
                        >
                          {label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/15 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-white/55">
          <div className="flex items-center gap-3">
            <HexMark size={14} strokeWidth={1.6} className="text-white/45" />
            <span>
              © {new Date().getFullYear()} {t.footer.copyright}
            </span>
          </div>
          <div className="flex items-center gap-6">
            <span>info@baltivex.com</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
